<template>
  <block-item-card
    ref="fixedVsFloatingRates"
    :title="$t('ALM.Section.LoansPortfolio.FixedFloatingComparison.Title')"
    :tooltip="$t('ALM.Section.LoansPortfolio.FixedFloatingComparison.InfoTip')"
    class="fill-height d-flex flex-column"
  >
    <div class="d-flex align-center ml-4">
      <div class="d-flex align-center mr-4">
        <div
          class="mr-2"
          style="width: 12px; height: 12px; background: #4CAF50; border-radius: 100%;"
        />
        <span class="text-size-12 synthesis-ui-grey-01--text">Fixed Rate</span>
      </div>
      <div class="d-flex align-center mr-4">
        <div
          class="mr-2"
          style="width: 12px; height: 12px; background: #2196F3; border-radius: 100%;"
        />
        <span class="text-size-12 synthesis-ui-grey-01--text">Floating Rate</span>
      </div>
    </div>

    <v-lazy
      :options="{threshold: .5}"
      transition="fade-transition"
    >
      <e-chart
        :ref="`echart${chartRefId}`"
        :autoresize="true"
        :option="chartOpts"
        :style="`height: 400px;`"
        class="chart"
      />
    </v-lazy>
  </block-item-card>
</template>

<script>
import BlockItemCard      from '@/theme/default/components/common/BlockItemCard'
import baseEChartColors   from '@/theme/default/components/charts/echarts/mixins/common/baseEChartColors'
import i18nRouteMeta                     from '@/mixins/i18n/i18nRouteMeta'
import riskBaseCurrency   from '@/theme/default/views/risks/mixins/riskBaseCurrency'
import baseEChartBarProps from '@/theme/default/components/charts/echarts/mixins/bar/baseEChartBarProps'
import baseEChartBarData  from '@/theme/default/components/charts/echarts/mixins/bar/baseEChartBarData'
import chartsColorMixin   from '@/theme/default/components/charts/mixins/chartsColorMixin'
import riskAnalysisId from '@/theme/default/views/risks/mixins/riskAnalysisId'
import { ScatterChart, BarChart } from 'echarts/charts'

// import { toNumberFormatNotationShort } from '@/lib/utils/helper'

import { use }                 from 'echarts/core'
import { UniversalTransition } from 'echarts/features'
import { CanvasRenderer }      from 'echarts/renderers'

import { GridComponent, LegendComponent, TitleComponent, TooltipComponent, TransformComponent, MarkPointComponent } from 'echarts/components'

use([
  BarChart,
  ScatterChart,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  TransformComponent,
  CanvasRenderer,
  UniversalTransition,
  MarkPointComponent
])

export default {
  name      : 'ALMFixedVsFloatingRates',
  components: { BlockItemCard },
  directives: {},
  mixins    : [i18nRouteMeta, riskBaseCurrency, baseEChartColors, baseEChartBarProps, baseEChartBarData, chartsColorMixin, riskAnalysisId],
  props     : {
    chartDataOptions: {
      type   : Array,
      default: () => []
    }
  },
  dataStore: {
    appToolbarExtended: 'App.Config.AppToolbarExtended',

    Loans: 'ALM.Loans.Data',

    riskBaseCurrencyData: 'Risks.BaseCurrency.Data'
  },
  enums: {},
  data () {
    return {
      breakpoint: (this.appToolbarExtended && this.$vuetify.breakpoint.smAndDown ? 12 : 6)
    }
  },
  computed: {
    fixedVsFloatingRates () {
      return this.Loans.FixedFloatingComparison || null
    },

    chartOpts () {
      if (!this.fixedVsFloatingRates) return {}

      const yAxis = this.fixedVsFloatingRates.labels.slice().reverse()
      // const fixedRateData    = (this.fixedVsFloatingRates.fixedRateData || [75, 75, 60, 50, 88, 75]).slice().reverse()

      const fixedRateData = this.fixedVsFloatingRates.perLabelFixedFloatingComparisonData.map(item => item.fixedRate).slice().reverse()
      const floatingRateData = this.fixedVsFloatingRates.perLabelFixedFloatingComparisonData.map(item => item.floatingRate).slice().reverse()
      // per-row coupons (can all differ)
      const fixedRateCouponData = this.fixedVsFloatingRates.perLabelFixedFloatingComparisonData.map(item => item.fixedRateCoupon).slice().reverse()
      const floatingRateCouponData = this.fixedVsFloatingRates.perLabelFixedFloatingComparisonData.map(item => item.floatingRateCoupon).slice().reverse()

      // chip width helper (keeps chip neat for 1–3 digits + %)
      const chipSize = (text) => {
        const w = Math.max(38, 8 * String(text).length + 16) // px
        const h = 20
        return [w, h]
      }
      const zeroPoints = (data, categories) => {
        const result = categories.map((cat, i) => data[i] === 0 ? {
          coord     : [50, cat], // middle of the track
          symbol    : 'rect',
          symbolSize: [1, 1], // tiny & invisible
          label     : {
            show         : true,
            position     : 'inside', // center on the markPoint
            align        : 'center',
            verticalAlign: 'middle',
            color        : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize     : 10,
            fontWeight   : 'bold',
            formatter    : '0%'
          }
        } : null).filter(Boolean)

        return result
      }

      return {
        animation        : true,
        animationDuration: 1000,
        animationEasing  : 'quinticOut',
        tooltip          : {
          show           : true,
          trigger        : 'axis',
          axisPointer    : { type: 'shadow' },
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#FFFFFF' },
          formatter      : (params) => {
            let result = `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].axisValue}</div>`
            params.forEach(param => {
              if (param.seriesName === 'Coupon') {
                result += `<div style="margin: 4px 0;">
              <span style="display:inline-block;width:12px;height:12px;background:#FF9800;margin-right:8px;border-radius:2px;"></span>
              <span>${param.seriesName}: ${param.data.coupon}%</span>
            </div>`
              } else {
                result += `<div style="margin: 4px 0;">
              <span style="display:inline-block;width:12px;height:12px;background:${param.color};margin-right:8px;border-radius:2px;"></span>
              <span>${param.seriesName}: ${param.value}%</span>
            </div>`
              }
            })
            return result
          }
        },
        legend: { show: false },
        grid  : {
          left        : 25,
          top         : 50,
          right       : 40, // give the chip room on the right
          bottom      : 0,
          containLabel: true
        },
        xAxis: { show: false },
        yAxis: {
          type     : 'category',
          data     : yAxis,
          axisLabel: {
            show    : true,
            color   : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize: 10
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: { show: false }
        },
        series: [
          // fixed
          {
            name     : 'Fixed Rate',
            type     : 'bar',
            data     : fixedRateData,
            itemStyle: { color: '#4CAF50', borderRadius: [0, 4, 4, 0] },
            label    : { show: true, color: this.themeColorRGBA('synthesis-ui-grey-02'), fontSize: 10, fontWeight: 'bold', formatter: (params) => { return params.value === 0 ? '' : params.value + '%' } },

            barWidth       : '35%',
            showBackground : true,
            backgroundStyle: { color: this.themeColorRGBA('synthesis-ui-grey-06') },
            markPoint      : { data: zeroPoints(fixedRateData, yAxis) }

          },
          // floating
          {
            name           : 'Floating Rate',
            type           : 'bar',
            data           : floatingRateData,
            label          : { show: true, color: this.themeColorRGBA('synthesis-ui-grey-02'), fontSize: 10, fontWeight: 'bold', formatter: (params) => { return params.value === 0 ? '' : params.value + '%' } },
            itemStyle      : { color: '#2196F3', borderRadius: [0, 4, 4, 0] },
            barWidth       : '35%',
            barGap         : '10%',
            showBackground : true,
            backgroundStyle: { color: this.themeColorRGBA('synthesis-ui-grey-06') },
            markPoint      : { data: zeroPoints(floatingRateData, yAxis) }

          },
          // coupon "chip" at the end of the background track
          {
            name        : 'Fixed Coupon',
            type        : 'scatter',
            symbol      : 'roundRect',
            symbolOffset: [25, -10],
            data        : yAxis.map((cat, i) => ({
              value : [100, cat],
              coupon: fixedRateCouponData[i]
            })),
            symbolSize: (val, params) => chipSize(`${params.data.coupon}%`),
            itemStyle : {
              color: 'white'

            },
            label: {
              show      : true,
              formatter : (p) => `${p.data.coupon}%`,
              position  : 'inside',
              color     : this.themeColorRGBA('synthesis-ui-orange-01'),
              fontWeight: 'bold',
              fontSize  : 10
            },
            tooltip: { show: false }
          },
          // floating coupon chip (slightly shifted up/down to avoid overlap)
          {
            name  : 'Floating Coupon',
            type  : 'scatter',
            symbol: 'roundRect',

            symbolOffset: [25, 10],
            data        : yAxis.map((cat, i) => ({
              // offset y by +0.25 to stack above/below
              value : [100, cat],
              coupon: floatingRateCouponData[i]
            })),
            symbolSize: (val, params) => chipSize(`${params.data.coupon}%`),
            itemStyle : {
              color: 'white'

            },
            label: {
              show      : true,
              formatter : (p) => `${p.data.coupon}%`,
              position  : 'inside',
              color     : this.themeColorRGBA('synthesis-ui-orange-01'),
              fontWeight: 'bold',
              fontSize  : 10
            },
            tooltip: { show: false }
          }
        ]
      }
    }
  },
  watch: {
    appToolbarExtended (val) {
      this.breakpoint = val && this.$vuetify.breakpoint.smAndDown ? 12 : 6
    },

    '$vuetify.breakpoint.smAndDown': {
      handler (val) {
        this.breakpoint = val && this.appToolbarExtended ? 12 : 6
      }
    }
  },
  created () {
  },
  beforeMount () {},
  mounted () {
  },
  beforeUpdate () {},
  updated () {},
  destroyed () {},
  methods: {

  }
}
</script>

<style scoped>
.chart {
  width: 100%;
  padding-right:20px;
}
</style>
